import React, { useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosRequestConfig } from "axios";
import { toast, Toaster } from "sonner";

// UI Components
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Img, Spinner } from "components";
import Navbar from "components/Navbar/Index";
import Stepper from "components/Stepper/newstepper";

// Redux
import { useAppDispatch, useAppSelector } from "redux/hooks";
import * as CreateOrderActions from "redux/features/CreateOrder";

// Services
import {
  POSTCreateOrderRequestType,
  ResumeOrderRequestType,
  UploadAllDocsRequestType,
  postcreateOrder,
  resumeOrder,
  uploadAllDocs,
} from "service/apis/CreateOrderapi";

// Models & Components
import Cancel from "models/Cancel";
import SendQuote from "models/CreateOrder/SendQuote";
import Step2 from "./step2";
import Step3 from "./step3";
import Step6 from "./step6";
import Step7 from "./step7";
import Step8 from "./step8";
import Step9 from "./step9";
import Step10 from "./step10";
import Step11 from "./step11";

// Utils
import { createOldOrderSteps, createOrderSteps, formatIndianNumber } from "../../utils";

// Constants
const VALIDATION_REGEX = {
  swift: /^[A-Z]{4}[-]{0,1}[A-Z]{2}[-]{0,1}[A-Z0-9]{2}[-]{0,1}[0-9]{3}$/,
  routing: /^\d{9}$/,
  transit: /^\d{5}$/,
  bsbCode: /^\d{3}-\d{3}$/,
  shortCode: /^\d{4}$/,
  ibanNumber: /^[A-Z]{2}\d{2}[A-Z0-9]{4}\d{7}([A-Z0-9]?){0,16}$/,
} as const;

const FINAL_STEPS = {
  RESUME: "Approve & Submit",
  CREATE: "Submit",
  CONTINUE: "Continue",
} as const;

// Types
interface MarginState {
  l1: string;
  l2: string;
  l3: string;
}

interface FileUploadResult {
  filename: string;
  file: string;
}

interface KycDocResult {
  doc_list_id: number;
  file_label: string;
  file: string;
}

const CreateOrder: React.FC = () => {
  // State Management
  const [state, setState] = useState({
    currentStep: 1,
    isCalcLoading: false,
    showQuote: false,
    loading: false,
    confirmCancel: false,
    confirmBenificiary: false,
    margin: { l1: "", l2: "", l3: "" } as MarginState,
  });

  // Hooks
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  // URL Parameters
  const orderType = useMemo(() =>
    new URLSearchParams(window.location.search).get("type") as "resume" | "",
    []
  );

  // Redux State
  const orderState = useAppSelector((state) => state.CreateOrder);
  const level = useMemo(() => localStorage.getItem("level"), []);

  // Computed Values
  const createOrderStepsFinal = useMemo(() =>
    location?.state?.ResumeOrderPage ? createOrderSteps : createOldOrderSteps,
    [location?.state?.ResumeOrderPage]
  );

  const isLastStep = useMemo(() =>
    createOrderStepsFinal.length === state.currentStep,
    [createOrderStepsFinal.length, state.currentStep]
  );

  // Update state helper
  const updateState = useCallback((updates: Partial<typeof state>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // File Conversion Utility
  const convertFileToString = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result.toString());
        } else {
          reject(new Error("Error reading file"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Validation Functions
  const validateBeneficiaryData = useCallback((): boolean => {
    const {
      service_partner_id,
      beneficiary_name,
      beneficiary_bank_name,
      beneficiary_bank_addr,
      beneficiary_country,
      beneficiary_swift_code,
      beneficiary_account_number,
      beneficiary_type,
      beneficiary_addr_line_1,
      beneficiary_addr_line_2,
      beneficiary_city,
      beneficiary_state,
      beneficiary_zip,
      beneficiary_transit_code,
      beneficiary_bsbcode,
      beneficiary_national_id,
      beneficiary_iban_no,
      beneficiary_routing_number,
      beneficiary_short_code,
    } = orderState;

    if (service_partner_id !== 17) return true;

    const requiredFields = [
      beneficiary_name,
      beneficiary_bank_name,
      beneficiary_bank_addr,
      beneficiary_country,
      beneficiary_swift_code,
      beneficiary_account_number,
      beneficiary_type,
    ];

    if (requiredFields.some(field => field === "")) {
      toast.error("Please enter beneficiary details");
      return false;
    }

    const addressFields = [
      beneficiary_addr_line_1,
      beneficiary_addr_line_2,
      beneficiary_city,
      beneficiary_state,
      beneficiary_zip,
      beneficiary_transit_code,
      beneficiary_bsbcode,
      beneficiary_national_id,
      beneficiary_iban_no,
    ];

    if (addressFields.some(field => field === "")) {
      toast.error("Please enter address and other details");
      return false;
    }

    // Regex validations
    const validations = [
      { value: beneficiary_swift_code, regex: VALIDATION_REGEX.swift, error: "Please enter a valid Swift Code" },
      { value: beneficiary_routing_number, regex: VALIDATION_REGEX.routing, error: "Please enter a valid Routing Number", optional: true },
      { value: beneficiary_transit_code, regex: VALIDATION_REGEX.transit, error: "Please enter a valid Transit Number" },
      { value: beneficiary_bsbcode, regex: VALIDATION_REGEX.bsbCode, error: "Please enter a valid BSB Code" },
      { value: beneficiary_short_code, regex: VALIDATION_REGEX.shortCode, error: "Please enter a valid Short Code", optional: true },
      { value: beneficiary_iban_no, regex: VALIDATION_REGEX.ibanNumber, error: "Please enter a valid IBAN Code" },
    ];

    for (const validation of validations) {
      if (!validation.optional && validation.value !== "" && !validation.regex.test(validation.value)) {
        toast.error(validation.error);
        return false;
      }
      if (validation.optional && validation.value !== "" && !validation.regex.test(validation.value)) {
        toast.error(validation.error);
        return false;
      }
    }

    return true;
  }, [orderState]);

  const getButtonState = useCallback(() => {
    switch (state.currentStep) {
      case 1:
        return orderState.deal_id === "";
      case 2:
        return !orderState.pan_found;
      default:
        return false;
    }
  }, [state.currentStep, orderState.deal_id, orderState.pan_found]);

  // API Functions
  const getAuthConfig = useCallback(() => {
    const auth = localStorage.getItem("authToken");
    if (!auth) throw new Error("No auth token found");
    return JSON.parse(auth);
  }, []);

  const handleUploadAllDocs = useCallback(async () => {
    try {
      const authToken = getAuthConfig();
      const formData = new FormData();

      formData.append("customers_id", orderState.customers_id);
      orderState.allDocs.forEach((doc) => {
        formData.append("docs[]", JSON.stringify(doc));
      });

      const payload: AxiosRequestConfig<UploadAllDocsRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: formData as any,
      };

      const response = await uploadAllDocs(payload);
      if (response.status === 200) {
        // Success handled in parent function
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Upload failed");
      throw error;
    }
  }, [orderState.customers_id, orderState.allDocs, getAuthConfig]);

  const handleUpdateResumeOrder = useCallback(async () => {
    try {
      updateState({ loading: true });
      const authToken = getAuthConfig();

      const docs = orderState.allDocs
        .filter((doc) => doc?.isEdited === true)
        .map((document) => ({
          doc_list_id: document?.doc_id === 0 ? null : document?.doc_id,
          file: document?.doc,
          filename: document?.doc_id === 0 ? document?.doc_name : "",
        }));

      const payload: AxiosRequestConfig<ResumeOrderRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: {
          order_id: orderState.order_id,
          customers_id: orderState.customers_id,
          document: docs,
          beneficiary2_id: orderState.selected_beneficiary_id,
        },
      };

      const response = await resumeOrder(payload);
      if (response.status === 200) {
        toast.success("Order resumed successfully");
        handleCancel();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Resume failed");
    } finally {
      updateState({ loading: false });
    }
  }, [orderState, getAuthConfig]);

  const processDocuments = useCallback(async () => {
    const arrayOfObjects = Object.entries(orderState.docs).map(async ([key, value]) => {
      try {
        const base64 = await convertFileToString(value);
        return { filename: key, file: base64 };
      } catch (error) {
        return null;
      }
    });

    const kyc_and_add_docs = [...orderState.kyc_docs, ...orderState.additional_docs];
    const newArrayOfObjects = kyc_and_add_docs.map(async (doc) => {
      try {
        const base64 = await convertFileToString(doc.file);
        return {
          doc_list_id: /^[-+]?\d+\.?\d*$|^[-+]?\.\d+$/.test(doc?.id) ? doc?.id : 0,
          file_label: doc?.file?.name,
          file: base64,
        };
      } catch (error) {
        return null;
      }
    });

    const [supportingDocs, kycDocs] = await Promise.all([
      Promise.all(arrayOfObjects),
      Promise.all(newArrayOfObjects),
    ]);

    return { supportingDocs: supportingDocs.filter(Boolean), kycDocs: kycDocs.filter(Boolean) };
  }, [orderState.docs, orderState.kyc_docs, orderState.additional_docs, convertFileToString]);

  const handleCreateOrder = useCallback(async () => {
    try {
      const authToken = getAuthConfig();
      const formData = new FormData();

      const { supportingDocs, kycDocs } = await processDocuments();

      // Append documents
      supportingDocs.forEach((element) => {
        formData.append(`supporting_documents2[]`, JSON.stringify(element));
      });

      kycDocs.forEach((element) => {
        formData.append(`kyc_and_add_doc[]`, JSON.stringify(element));
      });

      // Append form fields
      const formFields = {
        purpose_id: orderState.purpose_id,
        amount: orderState.amount,
        currency: orderState.currency,
        type: orderState.type,
        customers_id: orderState.customers_id,
        country: orderState.country,
        created_by: orderState.created_by,
        discount: orderState.discount,
        add_on_rates: orderState.add_on_rates?.toLocaleString(),
        reference_no: orderState.beneficiary_reference_number,
        beneficiary2_id: orderState.selected_beneficiary_id,
        l1_commission: orderState.l1_commission.toString(),
        l2_commission: orderState.l2_commission.toString(),
        l3_commission: orderState.l3_commission.toString(),
        extra_detail: JSON.stringify(orderState.extra_detail),
        tcs_type: "1",
      };

      Object.entries(formFields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      orderState.customer_disclosers_Ids.forEach((id) => {
        formData.append("customer_discloser_id[]", id.toString());
      });

      if (orderState.deal_id !== "") {
        formData.append("deals_id", orderState.deal_id);
      }

      const payload: AxiosRequestConfig<POSTCreateOrderRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: formData as any,
      };

      updateState({ loading: true });
      const response = await postcreateOrder(payload);

      localStorage.setItem("order_id", JSON.stringify(response.data.result.id));
      dispatch(CreateOrderActions.setcal_total_payable(response.data.result.payable_amount));

      if (response.data.remaining_orders !== -1) {
        toast.warning(`This partner can create only ${response.data.remaining_orders} more orders`);
      }

      updateState({ currentStep: state.currentStep + 1 });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Order creation failed");
    } finally {
      updateState({ loading: false });
    }
  }, [orderState, state.currentStep, getAuthConfig, processDocuments, dispatch]);

  // Navigation Functions
  const handleNext = useCallback(() => {
    if (isLastStep) return;

    switch (state.currentStep) {
      case 1:
        if (orderState.deal_id === "" || orderState.purpose_id === "" || orderState.country === "" || orderState.amount === "") {
          dispatch(CreateOrderActions.setshowerror(true));
          toast.error("Please fill all required fields");
          return;
        }
        dispatch(CreateOrderActions.setshowerror(false));
        updateState({ currentStep: state.currentStep + 1 });
        break;

      case 2:
        if (orderState.PAN.length !== 10) {
          toast.error("Please enter valid PAN number");
          return;
        }
        updateState({ currentStep: state.currentStep + 1 });
        break;

      case 4:
        const hasRequiredDocs = orderType === "resume" || orderState.required_docs !== 0;
        const hasValidAdditionalDocs = !Array.isArray(orderState.additional_docs) ||
          orderState.additional_docs.length === 0 ||
          !orderState.additional_docs.some((doc) => doc.file === null);

        if (!hasRequiredDocs || !hasValidAdditionalDocs) {
          toast.error("Please submit all the required documents and fields");
          return;
        }

        if (orderType === "resume") {
          handleUpdateResumeOrder();
        } else {
          Promise.all([handleUploadAllDocs(), handleCreateOrder()])
            .then(() => {
              toast.success("All documents uploaded and order created successfully!");
            })
            .catch((error) => {
              toast.error(error.message || "An error occurred while processing your request.");
            });
        }
        break;

      default:
        updateState({ currentStep: state.currentStep + 1 });
    }
  }, [state.currentStep, isLastStep, orderState, orderType, dispatch, handleUpdateResumeOrder, handleUploadAllDocs, handleCreateOrder]);

  const handlePrevious = useCallback(() => {
    if (state.currentStep <= 1) return;

    const prevStep = (state.currentStep === 4 && level !== "1")
      ? state.currentStep - 2
      : state.currentStep - 1;

    updateState({ currentStep: prevStep });
  }, [state.currentStep, level]);

  const resetOrderState = useCallback(() => {
    const actions = [
      CreateOrderActions.setPurposeId(""),
      CreateOrderActions.setCountry(""),
      CreateOrderActions.setAmount(""),
      CreateOrderActions.setType("beneficiary_receives"),
      CreateOrderActions.setCustomers_id(""),
      CreateOrderActions.setForeign_Bank_Charges(""),
      CreateOrderActions.setPAN(""),
      CreateOrderActions.setBeneficiaryName(""),
      CreateOrderActions.setBeneficiaryAddrLine1(""),
      CreateOrderActions.setBeneficiaryAddrLine2(""),
      CreateOrderActions.setBeneficiaryCity(""),
      CreateOrderActions.setBeneficiaryState(""),
      CreateOrderActions.setBeneficiaryCountry(""),
      CreateOrderActions.setBeneficiaryZip(""),
      CreateOrderActions.setBeneficiaryAccountNumber(""),
      CreateOrderActions.setBeneficiaryBankName(""),
      CreateOrderActions.setBeneficiaryBankAddr(""),
      CreateOrderActions.setBeneficiaryBankCountry(""),
      CreateOrderActions.setBeneficiarySwiftCode(""),
      CreateOrderActions.setBeneficiaryRoutingNumber(""),
      CreateOrderActions.setBeneficiaryTransitCode(""),
      CreateOrderActions.setBeneficiaryBsbcode(""),
      CreateOrderActions.setBeneficiaryShortCode(""),
      CreateOrderActions.setBeneficiaryNationalId(""),
      CreateOrderActions.setBeneficiaryIbanNo(""),
      CreateOrderActions.setBeneficiaryCorrespondantBankName(""),
      CreateOrderActions.setBeneficiaryCorrespondantBankNo(""),
      CreateOrderActions.setCreatedBy(""),
      CreateOrderActions.setDiscount(""),
      CreateOrderActions.setDocs({ filename: null, file: null }),
      CreateOrderActions.setAddOnRates(0),
      CreateOrderActions.setCurrency(""),
      CreateOrderActions.setWalkinOrder(false),
      CreateOrderActions.setForeign_Bank_Rates(""),
      CreateOrderActions.setTTRates(""),
      CreateOrderActions.setServicePartnerID(""),
      CreateOrderActions.setSettlementRate(""),
      CreateOrderActions.setCustomer_rate(""),
      CreateOrderActions.setDealID(""),
      CreateOrderActions.setFXCharges(""),
      CreateOrderActions.setL1CommissionRate(""),
      CreateOrderActions.setL2CommissionRate(""),
      CreateOrderActions.setL3CommissionRate(""),
      CreateOrderActions.setKycDocBase64({}),
      CreateOrderActions.setAdditionalDocBase64({}),
      CreateOrderActions.setAdditionalDocs([]),
      CreateOrderActions.setKycDocs([]),
      CreateOrderActions.setIsNameMismatch(false),
      CreateOrderActions.setAllDocs([]),
      CreateOrderActions.setSelectedBeneficiarieRowId(""),
      CreateOrderActions.setSelectedTableBeneficiaryRowId(""),
      CreateOrderActions.setBeneficiaryReferencenumber(""),
    ];

    actions.forEach(action => dispatch(action));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    localStorage.removeItem("order_id");
    resetOrderState();
    navigate("/remit/orders");
  }, [resetOrderState, navigate]);

  // Render Functions
  const renderStep = useCallback(() => {
    const stepComponents = {
      1: <Step2 setMargin2={(margin: MarginState) => updateState({ margin })} />,
      2: <Step3 />,
      3: <Step8 />,
      4: <Step7 />,
      5: <Step6 />,
    };
    return stepComponents[state.currentStep as keyof typeof stepComponents] || null;
  }, [state.currentStep]);

  const renderCalculationItem = useCallback((label: string, value: string | number, currency: string = "INR", isLoading: boolean = false) => (
    <div className="flex justify-between items-center">
      <span className="text-xs text-gray-600">{label}</span>
      {isLoading ? (
        <Skeleton className="h-4 bg-gray-300 w-[100px] rounded-full" />
      ) : (
        <span className="text-sm text-gray-900 float-right leading-4 flex items-center space-x-[2px] gap-1">
          {value === "" || value === "0.00" ? (
            "0.00"
          ) : (
            <>
              <span className="flex items-center bg-[#F5F5F5] border border-black-300 rounded-full px-2 py-0.5">
                {currency}
              </span>
              <span>{typeof value === 'number' ? value.toFixed(2) : value}</span>
            </>
          )}
        </span>
      )}
    </div>
  ), []);

  const getButtonText = useCallback(() => {
    if (location?.state?.ResumeOrderPage) {
      return state.currentStep === createOrderSteps.length - 1 ? FINAL_STEPS.RESUME : FINAL_STEPS.CONTINUE;
    }
    return state.currentStep === createOldOrderSteps.length - 1 ? FINAL_STEPS.CREATE : FINAL_STEPS.CONTINUE;
  }, [location?.state?.ResumeOrderPage, state.currentStep]);

  return (
    <>
      {/* Modals */}
      {state.confirmCancel && (
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
          className="fixed top-0 left-0 z-[10] h-screen w-[100vw] flex justify-center items-center"
        >
          <Cancel
            title="Are You Sure You Want To Cancel This Order?"
            setConfirmCancel={(value) => updateState({ confirmCancel: value })}
            handleCancel={handleCancel}
          />
        </div>
      )}

      {state.confirmBenificiary && (
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
          className="fixed top-0 left-0 z-50 h-screen w-[100vw] flex justify-center items-center"
        >
          <Cancel
            title={
              <div className="w-full flex flex-col justify-center items-center">
                <div>
                  <span className="font-[700]">A2 Form</span> will not be generated.
                </div>
                <div>Would you like to proceed?</div>
              </div>
            }
            setConfirmCancel={(value) => updateState({ confirmBenificiary: value })}
            handleCancel={() => updateState({ currentStep: state.currentStep + 1 })}
          />
        </div>
      )}

      {state.showQuote && (
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, .6)" }}
          className="fixed top-0 left-0 z-50 h-screen w-[100vw] flex justify-center items-center"
        >
          <SendQuote setShowQuote={(value) => updateState({ showQuote: value })} />
        </div>
      )}

      {state.loading && <Spinner />}

      <div className="p-4 bg-gray-100 flex flex-col font-inter items-start justify-start h-screen overflow-hidden mx-auto w-auto sm:w-full md:w-full">
        <div className="flex gap-5 flex-col w-full h-full">
          <Navbar>
            <div className="w-full flex justify-between">
              <div className="flex gap-3 items-center">
                <Img src="images/sidebar_img/remit.svg" className="h-5" alt="" />
                <div className="gap-2 flex flex-col">
                  <span className="text-sm font-semibold">Create Remittance Order</span>
                  <span className="text-xs font-normal text-[#999999]">
                    Create a remittance transaction using an unitilized deal
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="gap-2.5 flex items-center">
                  <Img src="images/overview/wrench.svg" alt="" />
                  <span className="text-sm font-semibold text-blue-700">Help</span>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger>
                    <button className="rounded-lg py-[11px] px-4 border bg-gray-button-gray-lite border-gray-border-gray-lite">
                      Cancel Order
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogPortal>
                    <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                    <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
                      <AlertDialogHeader className="flex text-left gap-4 pt-8 pb-4 px-10">
                        <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                          Are you absolutely sure?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="font-medium text-base leading-6 tracking-[2%] text-[#7E7E7E]">
                          This action can not be undone. This will permanently delete your progress and remove the actions preformed.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                        <AlertDialogCancel
                          className="m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                          asChild
                        >
                          <button>Close</button>
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                          asChild
                        >
                          <button onClick={handleCancel}>Continue</button>
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialogPortal>
                </AlertDialog>
              </div>
            </div>
          </Navbar>

          <div className="gap-5 w-full flex overflow-hidden h-full">
            <div className="bg-white-A700 p-5 rounded-xl flex flex-col gap-5 w-full flex-grow">
              <Stepper
                steps={createOrderStepsFinal.map((val) => val.name)}
                currentStep={state.currentStep - 1}
              />
              <div className="flex relative flex-col gap-5 w-full overflow-y-auto flex-grow">
                {renderStep()}
              </div>
              {state.currentStep !== 6 && (
                <div className="flex gap-3">
                  {state.currentStep !== 1 && (
                    <button
                      type="button"
                      onClick={handlePrevious}
                      className="px-4 py-[11px] w-fit rounded-lg border border-blue-700 text-blue-700"
                    >
                      Previous
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={getButtonState()}
                    onClick={handleNext}
                    className="px-4 py-[11px] w-fit rounded-lg disabled:opacity-40 bg-blue-700 text-white-A700"
                  >
                    {getButtonText()}
                  </button>
                </div>
              )}
            </div>

            {/* Calculations Section */}
            <section className="bg-white-A700 px-3 py-5 rounded-xl gap-4 w-[35%] flex flex-col">
              <p className="text-lg leading-4 font-semibold">Calculations</p>
              <div className="pt-5 pb-10 px-5 border-b-8 flex flex-col rounded-xl bg-custom-calc-gradient border-blue-700 gap-10">
                <div className="divide-y divide-neutral-400_1 gap-5 flex flex-col">
                  <div className="flex flex-col gap-4 w-full">
                    <div className="flex justify-between w-full">
                      <span className="text-xs leading-[14.5px] indent-3.5 text-[#666666]">Customer Rate</span>
                      {state.isCalcLoading ? (
                        <Skeleton className="h-4 bg-gray-600 w-[100px] rounded-full" />
                      ) : (
                        <span className="text-sm text-gray-900 float-right leading-4 flex items-center space-x-[2px] gap-1">
                          {orderState.customer_rate === "" || orderState.amount === "" ? (
                            "0.00"
                          ) : (
                            <>
                              <span className="flex items-center bg-[#F5F5F5] border border-black-300 rounded-full px-2 py-0.5">
                                INR
                              </span>
                              {parseFloat(orderState.customer_rate)?.toFixed(4)}
                            </>
                          )}
                        </span>
                      )}
                    </div>

                    <div className="flex justify-between w-full">
                      <span className="text-xs leading-[14.5px] indent-3.5 text-[#666666]">Recipient gets</span>
                      {state.isCalcLoading ? (
                        <Skeleton className="h-4 bg-gray-600 w-[100px] rounded-full" />
                      ) : (
                        <span className="text-sm text-gray-900 float-right leading-4 flex items-center space-x-[2px] gap-1">
                          {orderState.cal_amount_inr === "" || orderState.amount === "" ? (
                            "0.00"
                          ) : (
                            <>
                              <span className="flex items-center bg-[#F5F5F5] border border-black-300 rounded-full px-2 py-0.5">
                                {orderState.currency === "" || orderState.currency === undefined
                                  ? "USD "
                                  : `${orderState.currency} `}
                              </span>
                              {formatIndianNumber(parseFloat(orderState.amount)?.toFixed(4)).toString()}
                            </>
                          )}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col w-full gap-6 p-4 bg-white rounded-lg">
                      <div className="flex justify-between w-full border-b border-gray-300 pb-4">
                        <span className="text-xs font-medium text-[#666666]">Total Charges</span>
                        {state.isCalcLoading ? (
                          <Skeleton className="h-4 bg-gray-300 w-[100px] rounded-full" />
                        ) : (
                          <span className="text-sm float-right leading-4 flex items-center text-[#333333] space-x-[2px] gap-1">
                            {orderState.cal_total_charges === "" || orderState.amount === "" ? (
                              "0.00"
                            ) : (
                              <>
                                <span className="bg-[#F5F5F5] flex items-center border border-black-300 rounded-full px-2 py-0.5">
                                  INR
                                </span>
                                <span>{parseFloat(Number(orderState.cal_total_charges)?.toFixed(2))}</span>
                              </>
                            )}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-4">
                        {renderCalculationItem(
                          "GST on FX Conversion",
                          orderState.cal_gst === "" || orderState.amount === "" ? "0.00" : Number(orderState.cal_gst)?.toFixed(2),
                          "INR",
                          state.isCalcLoading
                        )}

                        {Number(orderState.tcs) > 0 && renderCalculationItem(
                          "TCS (incl. GST)",
                          orderState.tcs === "" ? "0.00" : Number(orderState.tcs)?.toFixed(2),
                          "INR",
                          state.isCalcLoading
                        )}

                        {renderCalculationItem(
                          "Foreign Bank Charges (incl. GST)",
                          orderState.cal_foreign_bank_charges === "" || orderState.amount === ""
                            ? "0.00"
                            : Number(orderState.cal_foreign_bank_charges)?.toFixed(2),
                          "INR",
                          state.isCalcLoading
                        )}

                        {renderCalculationItem(
                          "TT Charges (incl. GST)",
                          orderState.cal_tt_charges === "" || orderState.amount === ""
                            ? "0.00"
                            : Number(orderState.cal_tt_charges)?.toFixed(2),
                          "INR",
                          state.isCalcLoading
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-2.5 text-blue-700 w-full">
                    <span className="text-xs leading-[14.5px] indent-3.5 font-semibold">Total estimation</span>
                    {state.isCalcLoading ? (
                      <Skeleton className="h-4 bg-gray-600 w-[100px] rounded-full" />
                    ) : (
                      <span className="text-xs font-semibold text-blue-700 float-right leading-4 indent-3.5">
                        {orderState.cal_total_payable === "" || orderState.amount === "" ? (
                          "0.00"
                        ) : (
                          <>
                            <span className="bg-[#E8F0FF] indent-3.5 border border-blue-300 rounded-full px-2 py-0.5">
                              INR
                            </span>{" "}
                            {formatIndianNumber(parseFloat(orderState.cal_total_payable)?.toFixed(2))}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 h-full flex justify-end items-end">
                <div className="flex flex-col gap-3 p-2">
                  <p className="font-normal leading-[19.2px] text-xs text-[#666666]">
                    The above amount is an estimate and may change based on real time fx rates and remitter's current FY transactions.
                  </p>
                  <h4 className="font-medium leading-[19.2px] text-xs text-[#232323]">
                    Decimals are rounded off.
                  </h4>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
    </>
  );
};

export default CreateOrder;